/**
 * Севооборот — арифметика и правила года. Чистые функции, без React и без API.
 *
 * Всё, что здесь лежит, агроном проверяет глазами прямо в форме: сколько
 * гектаров осталось, законна ли цепочка «рапс → просо», что уйдёт в
 * предшественники следующего года. Поэтому логика вынесена отдельно и покрыта
 * тестами — на экране она обязана совпадать с тем, что посчитает сервер.
 *
 * Термины — агрономические: «поле поделено» / «одна за другой», без слов
 * из модели (leaf, XOR, occupancy).
 */

/** Как культуры делили год. Совпадает с `mode` в API севооборота. */
export type YearMode = 'single' | 'parallel' | 'sequential';

export interface DraftEntry {
  kind: 'crop' | 'fallow';
  /** Ключ выбранной культуры из справочника хозяйства (или '' — не выбрана). */
  cropKey: string;
  cropName?: string;
  fallowKind?: string;
  /** Введённая площадь, га. null — «не знаем». */
  areaHa: number | null;
  /** Введённая доля поля, %. Живёт рядом с га: переключение пересчитывает. */
  sharePct: number | null;
  /** Что агроном вводит руками сейчас — от этого зависит, что пересчитываем. */
  inputAs: 'ha' | 'pct';
  yieldCHa: number | null;
  sowingDate: string | null;
  harvestDate: string | null;
  varietyIds: string[];
  isPerennial: boolean;
  perennialStartYear: number | null;
  cutsCHa: number[];
  terminationKind: string | null;
  terminationDate: string | null;
}

/**
 * Значение строки выпадашки «чем занято поле».
 *
 * Культуры и пар/АМП живут в ОДНОМ списке (решение founder, раздел 4
 * wireframe): агроном ищет «Пар чёрный» там же, где ищет «Подсолнечник», —
 * для него это один вопрос «чем занято поле». Приходят они из двух источников
 * (`/tenant-crops/picker` не отдаёт пар), поэтому склейка и разбор значения —
 * здесь, а не в разметке.
 */
export const FALLOW_PREFIX = 'fallow:';

export function fallowOptionValue(code: string): string {
  return `${FALLOW_PREFIX}${code}`;
}

export function isFallowValue(value: string): boolean {
  return value.startsWith(FALLOW_PREFIX);
}

/** Разбирает выбранное значение обратно в поля занятия. */
export function parseOccupationValue(
  value: string,
  cropName?: string,
): Pick<DraftEntry, 'kind' | 'cropKey' | 'cropName' | 'fallowKind'> {
  if (isFallowValue(value)) {
    return {
      kind: 'fallow',
      cropKey: '',
      cropName: undefined,
      fallowKind: value.slice(FALLOW_PREFIX.length),
    };
  }
  return { kind: 'crop', cropKey: value, cropName, fallowKind: undefined };
}

/** Что показывать выбранным в выпадашке занятия. */
export function occupationValue(entry: DraftEntry): string {
  if (entry.kind === 'fallow') {
    return entry.fallowKind ? fallowOptionValue(entry.fallowKind) : '';
  }
  return entry.cropKey;
}

/**
 * Ключ уже записанного занятия для выпадашки культур.
 *
 * ОБЯЗАН совпадать с `cropPickerKey` из `card/api.ts` — выпадашка адресует
 * строки id номенклатуры ХОЗЯЙСТВА (`local_catalog_item_id`), а канонический
 * `crop_id` использует только как запасной ключ для культур вне номенклатуры.
 * Пока форма строила ключ как `crop:<crop_id>`, значение не совпадало ни с
 * одной строкой списка, и Radix показывал плейсхолдер — форма правки
 * импортированного года открывалась «пустой» (UAT founder 08.08).
 *
 * Держим формулу здесь и проверяем тестом против `cropPickerKey`, чтобы две
 * стороны не разъехались снова.
 */
export function cropKeyFromEntry(entry: {
  local_catalog_item_id?: string | null;
  crop_id?: string | null;
}): string {
  if (entry.local_catalog_item_id) return entry.local_catalog_item_id;
  return entry.crop_id ? `crop:${entry.crop_id}` : '';
}

/**
 * Нужна ли «сиротская» строка в выпадашке.
 *
 * Культура записана, но её нет в текущем списке хозяйства (архивировали,
 * переименовали, ещё не подтянулась) — показать плейсхолдер значит соврать,
 * будто поле пустое, и молча затереть запись при сохранении. Вместо этого
 * подставляем строку с сохранённым именем.
 */
export function missingCropOption(
  cropKey: string,
  cropName: string | null | undefined,
  optionKeys: ReadonlyArray<string>,
): { value: string; label: string } | null {
  if (!cropKey || optionKeys.includes(cropKey)) return null;
  return { value: cropKey, label: cropName?.trim() || 'Культура вне справочника' };
}

/** Пустая строка занятия — с неё начинается ввод любого года. */
export function emptyEntry(): DraftEntry {
  return {
    kind: 'crop',
    cropKey: '',
    areaHa: null,
    sharePct: null,
    inputAs: 'ha',
    yieldCHa: null,
    sowingDate: null,
    harvestDate: null,
    varietyIds: [],
    isPerennial: false,
    perennialStartYear: null,
    cutsCHa: [],
    terminationKind: null,
    terminationDate: null,
  };
}

/**
 * Проценты → гектары. Возвращает `null`, когда считать не из чего —
 * это «не знаем», а не ноль.
 */
export function haFromPct(pct: number | null, fieldAreaHa: number | null): number | null {
  if (pct == null || !fieldAreaHa) return null;
  return round2((fieldAreaHa * pct) / 100);
}

/** Гектары → проценты поля (одна цифра после запятой — больше не читается). */
export function pctFromHa(ha: number | null, fieldAreaHa: number | null): number | null {
  if (ha == null || !fieldAreaHa) return null;
  return Math.round((ha / fieldAreaHa) * 1000) / 10;
}

/**
 * Переключение «га ⇄ %» ПЕРЕСЧИТЫВАЕТ введённое, а не стирает.
 *
 * Это прямое требование wireframe: агроном, набравший 288 га и нажавший «%»,
 * должен увидеть 60 %, а не пустое поле и заново вводить.
 */
export function switchUnit(
  entry: DraftEntry,
  to: 'ha' | 'pct',
  fieldAreaHa: number | null,
): DraftEntry {
  if (entry.inputAs === to) return entry;
  if (to === 'pct') {
    return { ...entry, inputAs: 'pct', sharePct: pctFromHa(entry.areaHa, fieldAreaHa) };
  }
  return { ...entry, inputAs: 'ha', areaHa: haFromPct(entry.sharePct, fieldAreaHa) };
}

/** Площадь занятия в гектарах независимо от того, как её вводили. */
export function effectiveAreaHa(entry: DraftEntry, fieldAreaHa: number | null): number | null {
  return entry.inputAs === 'pct' ? haFromPct(entry.sharePct, fieldAreaHa) : entry.areaHa;
}

export interface AreaCheck {
  /** Сколько гектаров поля занято занятиями года. */
  occupiedHa: number;
  /** Сколько осталось. Отрицательное = перебор. */
  remainingHa: number | null;
  overflow: boolean;
  message: string | null;
}

/**
 * Сумма частей поля против его площади.
 *
 * Правило founder: «больше площади поля занять нельзя; меньше — можно,
 * остаток останется незанятым (это тоже факт истории)». Поэтому недобор
 * НЕ ошибка, а перебор — блокирует сохранение.
 *
 * У цепочки «одна за другой» части не складываются: обе культуры занимали
 * поле целиком, просто в разное время.
 */
export function checkArea(
  entries: DraftEntry[],
  mode: YearMode,
  fieldAreaHa: number | null,
): AreaCheck {
  const areas = entries.map((e) => effectiveAreaHa(e, fieldAreaHa) ?? 0);
  const occupied = mode === 'sequential' ? Math.max(0, ...areas) : areas.reduce((a, b) => a + b, 0);
  const occupiedHa = round2(occupied);
  if (!fieldAreaHa) {
    return { occupiedHa, remainingHa: null, overflow: false, message: null };
  }
  const remaining = round2(fieldAreaHa - occupiedHa);
  // Допуск 0,01 га — округление процентов не должно блокировать сохранение.
  const overflow = occupiedHa > fieldAreaHa + 0.01;
  return {
    occupiedHa,
    remainingHa: remaining,
    overflow,
    message: overflow
      ? `Части занимают ${fmtHa(occupiedHa)}, а поле — ${fmtHa(fieldAreaHa)}. ` +
        'Больше площади поля занять нельзя.'
      : null,
  };
}

/**
 * Цепочка «одна за другой»: вторую сеяли ПОСЛЕ уборки первой.
 *
 * Это не формальность — от порядка зависит предшественник, то есть смысл всей
 * таблицы. Проверяем только заполненные даты: за 2018 год точных дат может не
 * быть вовсе, и требовать их значит убить скорость ввода.
 */
export function checkChain(entries: DraftEntry[]): { ok: boolean; message: string | null } {
  for (let i = 1; i < entries.length; i += 1) {
    const prev = entries[i - 1];
    const cur = entries[i];
    if (prev.harvestDate && cur.sowingDate && cur.sowingDate < prev.harvestDate) {
      return {
        ok: false,
        message:
          `Вторую культуру сеяли ${fmtDate(cur.sowingDate)}, а предыдущую убрали позже — ` +
          `${fmtDate(prev.harvestDate)}. Пожнивной посев идёт после уборки предыдущей.`,
      };
    }
  }
  return { ok: true, message: null };
}

/**
 * Чем поле уйдёт в следующий год.
 *
 * Правило founder: у цепочки предшественник — ПОСЛЕДНЯЯ культура; у
 * поделённого поля предшественник разный на разных частях, и врать
 * «главной культурой» здесь нельзя.
 */
export function predecessorHint(entries: DraftEntry[], mode: YearMode): string | null {
  const named = entries.filter((e) => e.cropName || e.fallowKind);
  if (named.length === 0) return null;
  if (named.length === 1 || mode === 'single') return named[0].cropName ?? 'пар';
  if (mode === 'sequential') return named[named.length - 1].cropName ?? 'пар';
  return 'по частям поля';
}

/** Можно ли сохранить год: все занятия названы, площади сходятся, цепочка законна. */
export function canSaveYear(
  entries: DraftEntry[],
  mode: YearMode,
  fieldAreaHa: number | null,
  year = Number.POSITIVE_INFINITY,
): { ok: boolean; message: string | null } {
  if (entries.length === 0) return { ok: true, message: null }; // пустой год = стереть
  const badPerennial = entries.find(
    (e) => e.isPerennial && e.perennialStartYear != null && e.perennialStartYear > year,
  );
  if (badPerennial) {
    return {
      ok: false,
      message: `Год посева трав не может быть позже ${year} — это редактируемый год.`,
    };
  }
  const unnamed = entries.find((e) =>
    e.kind === 'crop' ? !e.cropKey : !e.fallowKind,
  );
  if (unnamed) {
    return {
      ok: false,
      message:
        unnamed.kind === 'crop'
          ? 'Выберите культуру — без неё год не сохранится.'
          : 'Выберите, чем поле было занято (пар или АМП).',
    };
  }
  const withYield = entries.find((e) => e.kind === 'fallow' && e.yieldCHa != null);
  if (withYield) {
    return { ok: false, message: 'У пара урожайности не бывает — очистите поле.' };
  }
  const area = checkArea(entries, mode, fieldAreaHa);
  if (area.overflow) return { ok: false, message: area.message };
  if (mode === 'sequential') {
    const chain = checkChain(entries);
    if (!chain.ok) return chain;
  }
  return { ok: true, message: null };
}

/**
 * Следующий ПУСТОЙ год — цель главной кнопки «Сохранить и перейти к …».
 *
 * Именно пустой, а не «следующий по порядку»: за один заход агроном закрывает
 * дырявую историю, не возвращаясь в список. `null` — дырок больше нет.
 */
export function nextEmptyYear(emptyYears: number[], afterYear: number): number | null {
  const older = emptyYears.filter((y) => y < afterYear).sort((a, b) => b - a);
  if (older.length) return older[0];
  const rest = emptyYears.filter((y) => y !== afterYear).sort((a, b) => b - a);
  return rest.length ? rest[0] : null;
}

/** Чем многолетник ушёл с поля. Коды совпадают с `TERMINATION_KINDS` в API. */
export const TERMINATION_OPTIONS: Array<{ value: string; label: string }> = [
  { value: 'plow_under', label: 'Запашка' },
  { value: 'disking', label: 'Дискование' },
  { value: 'other', label: 'Другое' },
];

/**
 * Предупреждение «травы не закрыты».
 *
 * Пока терминирующая операция не указана, посев ЖИВОЙ и занимает поле в
 * текущем сезоне. Это ровно тот случай, когда «пустое поле» в системе означает
 * «забыли закрыть травы» — экран обязан это подсвечивать. Но это НЕ ошибка:
 * второй год трёхлетней люцерны законно не имеет терминации.
 */
export function perennialOpenWarning(entry: DraftEntry): string | null {
  if (!entry.isPerennial || entry.terminationKind) return null;
  return (
    'Пока не указано, чем закончился посев, травы считаются живыми и занимают ' +
    'поле в текущем сезоне. Если это не последний год — так и должно быть.'
  );
}

/**
 * Год посева переходящего посева.
 *
 * Если агроном не сказал иного, годом посева считается редактируемый год —
 * то есть «1-й год». Для 2-го и 3-го года он обязан указать год посева, иначе
 * сервер заведёт НОВЫЙ сев, и история покажет три сева там, где был один.
 */
export function perennialStartYear(entry: DraftEntry, year: number): number {
  return entry.perennialStartYear ?? year;
}

/** «1-й год» / «2-й год» — как многолетник читается в таблице. */
export function perennialYearLabel(n: number | null | undefined): string | null {
  if (!n || n < 1) return null;
  return `${n}-й год`;
}

/** Урожайность года многолетника = сумма укосов. */
export function sumCuts(cuts: number[]): number | null {
  const clean = cuts.filter((c) => Number.isFinite(c));
  if (!clean.length) return null;
  return Math.round(clean.reduce((a, b) => a + b, 0) * 10) / 10;
}

// ── форматирование ────────────────────────────────────────────────────────

export function round2(n: number): number {
  return Math.round(n * 100) / 100;
}

/** «480 га» / «472,5 га». Пусто → «—», а не «0 га». */
export function fmtHa(n: number | null | undefined): string {
  if (n == null || !Number.isFinite(n)) return '—';
  return `${n.toLocaleString('ru-RU', { maximumFractionDigits: 1 })} га`;
}

/**
 * Урожайность, ц/га. Пустое ≠ ноль: пусто означает «не знаем», а «0» —
 * «убрали и ничего не получили» (вымерзло, списали). Экран их не путает.
 */
export function fmtYield(n: number | null | undefined): string {
  if (n == null || !Number.isFinite(n)) return '—';
  return n.toLocaleString('ru-RU', { minimumFractionDigits: 1, maximumFractionDigits: 1 });
}

/** ISO-дата → «12.07.2025». Пусто → «—». */
export function fmtDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  const [y, m, d] = iso.split('-');
  return d && m && y ? `${d}.${m}.${y}` : iso;
}
